/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { Debate, Message, DebateView } from '../types';
import { getDebates, getDebateById, createDebate, generateDebateViews, getActiveDebates, getCompletedDebates, getUserDebates } from '../services/debateService';
import { api } from '../services/api';
import { toast } from 'react-hot-toast';
import { useAuth } from './AuthContext'; // Import the AuthContext 
import axios from 'axios';
import logger from '../utils/logger';

// DISABLE CONSOLE LOGGING FOR CONTEXT
// Store original console methods
const originalConsoleLog = console.log;
const originalConsoleError = console.error;
const originalConsoleWarn = console.warn;

// Replace console.log with suppression for DebateContext logs
console.log = function(...args: any[]) {
  // Suppress all console logs from this file
  if (args[0] && typeof args[0] === 'string' && args[0].includes('DebateContext:')) {
    return;
  }
  // Pass through other logs
  originalConsoleLog.apply(console, args);
};

// Replace console.error with suppression for DebateContext logs
console.error = function(...args: any[]) {
  // Suppress all console errors from this file
  if (args[0] && typeof args[0] === 'string' && args[0].includes('DebateContext:')) {
    return;
  }
  // Pass through other errors
  originalConsoleError.apply(console, args);
};

// Replace console.warn with suppression for DebateContext logs
console.warn = function(...args: any[]) {
  // Suppress all console warnings from this file
  if (args[0] && typeof args[0] === 'string' && args[0].includes('DebateContext:')) {
    return;
  }
  // Pass through other warnings
  originalConsoleWarn.apply(console, args);
};

// API URL configuration
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api'; 

// Add flag to check if we should use Pusher instead of Socket.IO
const USE_PUSHER = process.env.REACT_APP_USE_PUSHER === 'true';

// Optional environment debug (disabled in staging/production)
if (process.env.NODE_ENV === 'development') {
  logger.debug('ENV DEBUG', {
    REACT_APP_USE_PUSHER: process.env.REACT_APP_USE_PUSHER,
    USE_PUSHER,
    NODE_ENV: process.env.NODE_ENV,
  });
}

// For Vercel deployment, modify socket URL to point to the serverless function
const getSocketUrl = () => {
  // Skip if using Pusher
  if (USE_PUSHER) {
    logger.info('DebateContext: Using Pusher instead of Socket.IO - getSocketUrl skipped');
    return '';
  }
  
  // If we're using a custom socket URL from env, use that
  if (process.env.REACT_APP_SOCKET_URL) {
    logger.info('DebateContext: Using socket URL from environment:', process.env.REACT_APP_SOCKET_URL);
    return process.env.REACT_APP_SOCKET_URL;
  }
  
  // For Vercel deployment using the same domain
  if (window.location.hostname === 'app.gauntletdebates.com') {
    logger.info('DebateContext: Using API domain for socket URL');
    return 'https://api.gauntletdebates.com'; // Your API domain
  }
  
  // For Vercel preview deployments or other environments
  if (window.location.hostname.includes('vercel.app')) {
    // Extract the deployment URL and point to the socket handler
    const baseUrl = window.location.origin;
    const socketUrl = baseUrl.replace('gauntletdebates-client', 'gauntletdebates');
    logger.info('DebateContext: Using derived socket URL for Vercel deployment:', socketUrl);
    return socketUrl;
  }
  
  // Default to localhost for development
  logger.info('DebateContext: Using localhost socket URL');
  return 'http://localhost:5000';
};

// Fix API paths to avoid duplicate /api
const makeApiPath = (path: string) => {
  // If API_BASE already includes /api, don't add it again
  if (API_BASE.endsWith('/api')) {
    return `${API_BASE}${path.startsWith('/') ? path : '/' + path}`;
  }
  
  // Otherwise, include /api in the path
  return `${API_BASE}/api${path.startsWith('/') ? path : '/' + path}`;
};

// Define the context type
interface DebateContextType {
  debates: Debate[];
  activeDebates: Debate[];
  completedDebates: Debate[];
  userDebates: Debate[];
  currentDebate: Debate | null;
  loading: boolean;
  error: string | null;
  socket: Socket | null;
  fetchDebates: () => Promise<void>;
  fetchActiveDebates: () => Promise<void>;
  fetchCompletedDebates: () => Promise<void>;
  fetchUserDebates: () => Promise<void>;
  debouncedFetchActiveDebates: () => void;
  debouncedFetchCompletedDebates: () => void;
  debouncedFetchUserDebates: () => void;
  fetchDebateById: (id: string, forceRefresh?: boolean) => Promise<void>;
  startNewDebate: (
    topic: string,
    description?: string,
    additionalData?: {
      views?: { prompt: string; counterPrompt: string }[];
      teams?: { name: string; viewpoint: string; isAI: boolean; maxMembers?: number; publicSpots?: number }[];
      parameters?: {
        numberOfTeams: number;
        topic: string;
        timeLimit?: number;
        maxMessages?: number;
      };
      isPublic?: boolean;
      publicJoinable?: boolean;
      invitePasswordEnabled?: boolean;
      invitePassword?: string;
    }
  ) => Promise<Debate>;
  generateViews: (topic: string, description?: string) => Promise<DebateView>;
  sendMessage: (debateId: string, content: string, teamId: string, isReformulation: boolean) => Promise<Message>;
  liftFlag: (debateId: string, messageId: string, flagId: string) => void;
  currentTeamWithFlag: string | null;
  setCurrentTeamWithFlag: React.Dispatch<React.SetStateAction<string | null>>;
  processingMessages: {[key: string]: boolean};
  handleAIAnalysisComplete: (messageId: string, hasFlags: boolean, senderId?: string) => void;
  startPollingForMessage: (debateId: string, messageId: string) => void;
  startFallbackPolling: (debateId: string, messageId: string) => void; // NEW: Fallback polling for receivers
  startPollingForCubeAI: (debateId: string) => void; // NEW: Poll for CUBE AI response
}

// Create the context with default values
const DebateContext = createContext<DebateContextType>({
  debates: [],
  activeDebates: [],
  completedDebates: [],
  userDebates: [],
  currentDebate: null,
  loading: false,
  error: null,
  socket: null,
  fetchDebates: async () => {},
  fetchActiveDebates: async () => {},
  fetchCompletedDebates: async () => {},
  fetchUserDebates: async () => {},
  fetchDebateById: async () => {},
  startNewDebate: async () => ({} as Debate),
  generateViews: async () => ({} as DebateView),
  sendMessage: async () => ({} as Message),
  liftFlag: () => {},
  debouncedFetchActiveDebates: () => {},
  debouncedFetchCompletedDebates: () => {},
  debouncedFetchUserDebates: () => {},
  currentTeamWithFlag: null,
  setCurrentTeamWithFlag: () => {},
  processingMessages: {},
  handleAIAnalysisComplete: () => {},
  startPollingForMessage: () => {},
  startFallbackPolling: () => {}, // NEW: Default implementation  
  startPollingForCubeAI: () => {}, // NEW: Default implementation
});

// Custom hook to use the debate context
export const useDebate = () => useContext(DebateContext);

// Provider component
export const DebateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth(); // Access the authenticated user
  const [debates, setDebates] = useState<Debate[]>([]);
  const [activeDebates, setActiveDebates] = useState<Debate[]>([]);
  const [completedDebates, setCompletedDebates] = useState<Debate[]>([]);
  const [userDebates, setUserDebates] = useState<Debate[]>([]);
  const [currentDebate, setCurrentDebate] = useState<Debate | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [currentTeamWithFlag, setCurrentTeamWithFlag] = useState<string | null>(null);
  // Track messages that are currently being processed (waiting for AI analysis)
  const [processingMessages, setProcessingMessages] = useState<{[key: string]: boolean}>({});
  // Track the current debate ID for socket reconnection
  const previousDebateIdRef = useRef<string | null>(null);

  // Add debate cache to prevent excessive fetching - use ref to avoid re-render loops
  const debateCacheRef = useRef<{[key: string]: {debate: Debate, timestamp: number}}>({});
  const activeFetchesRef = useRef<{[key: string]: Promise<void>}>({});
  const CACHE_DURATION = 30000; // 30 seconds cache - balanced between freshness and performance

  // Restore original console methods on unmount
  useEffect(() => {
    return () => {
      console.log = originalConsoleLog;
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
    };
  }, []);

  // Add a state variable to track if fetches are in progress
  const [isFetching, setIsFetching] = useState<{
    active: boolean;
    completed: boolean;
    debates: boolean;
    user: boolean;
  }>({
    active: false,
    completed: false,
    debates: false,
    user: false,
  });

  // Add a ref to track current debates to prevent unnecessary fetches
  const currentDebateIdRef = React.useRef<string | null>(null);

  // Add a debounce timeout reference at the top of the provider component
  const [debounceTimeouts, setDebounceTimeouts] = useState<{
    active: NodeJS.Timeout | null;
    completed: NodeJS.Timeout | null;
    debates: NodeJS.Timeout | null;
    user: NodeJS.Timeout | null;
  }>({
    active: null,
    completed: null,
    debates: null,
    user: null,
  });

  // Helper function to update a debate in a list - declare early since it's used by fetchDebateById
  const updateDebateInList = (list: Debate[], updatedDebate: Debate): Debate[] => {
    const existingIndex = list.findIndex(d => d.id === updatedDebate.id);
    if (existingIndex >= 0) {
      // Replace existing debate with updated one
      return [
        ...list.slice(0, existingIndex),
        updatedDebate,
        ...list.slice(existingIndex + 1)
      ];
    }
    // If not found, return the original list
    return list;
  };

  // Use the imported getDebateById from debateService (which uses authenticated api instance)
  const getDebateByIdWithAuth = async (debateId: string): Promise<Debate> => {
    try {
      logger.info('DebateContext: API call to fetch debate by ID:', debateId);
      
      // Use the imported getDebateById which uses the authenticated api instance
      const debate = await getDebateById(debateId);
      
      // Check if there's a reformulation request and update the UI state
      if (debate.reformulationRequest && debate.reformulationRequest.teamId) {
        logger.info('DebateContext: Found reformulation request for team:', debate.reformulationRequest.teamId);
        // Update the currentTeamWithFlag state
        setCurrentTeamWithFlag(debate.reformulationRequest.teamId);
      }
      
      return debate;
    } catch (error) {
      logger.error('DebateContext: Error in getDebateById:', error);
      throw error;
    }
  };

  // Fetch a debate by ID - move before the socket handlers
  const fetchDebateById = useCallback(async (id: string, forceRefresh: boolean = false): Promise<void> => {
    if (!id) return;
    
    // Check if there's already an active fetch for this debate
    const existingFetch = activeFetchesRef.current[id];
    if (!forceRefresh && existingFetch) {
      logger.info(`DebateContext: 🚫 SKIPPED - Already fetching debate ${id}, waiting for existing fetch`);
      return existingFetch;
    }
    
    // Check cache first (unless forceRefresh is true)
    if (!forceRefresh && debateCacheRef.current[id]) {
      const cached = debateCacheRef.current[id];
      const now = Date.now();
      if (now - cached.timestamp < CACHE_DURATION) {
        logger.info(`DebateContext: 🎯 CACHED - Using cached debate ${id} (${Math.round((now - cached.timestamp) / 1000)}s old)`);
        setCurrentDebate(cached.debate);
        return;
      } else {
        logger.info(`DebateContext: ⏰ EXPIRED - Cache expired for debate ${id}, fetching fresh data`);
      }
    }
    
    // Don't fetch if we're already fetching this ID unless forceRefresh is true
    if (currentDebateIdRef.current === id && !forceRefresh) {
      logger.info(`DebateContext: 🚫 SKIPPED - Already fetching debate ${id}, skipping duplicate fetch`);
      return;
    }
    
    logger.info(`DebateContext: 🔄 FETCHING - Debate by ID: ${id}, forceRefresh: ${forceRefresh}`);
    currentDebateIdRef.current = id;
    
    // Create and store the fetch promise
    const fetchPromise = (async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getDebateByIdWithAuth(id);
      logger.info(`DebateContext: Successfully fetched debate ${id}`);
      logger.info(`DebateContext: Debate status: ${response.status}, has summary: ${!!response.summary}`);
      
      if (response.summary) {
        logger.info(`DebateContext: Summary length: ${response.summary.length}`);
      }
      
      setCurrentDebate(response);
      
      // NEW: Check for processing messages and resume polling
      if (response.messages && response.messages.length > 0) {
        response.messages.forEach((message: any) => {
          if (message.isProcessing && message.id) {
            logger.info('🔄 RESUME POLLING: Found processing message on page load, resuming polling', {
              messageId: message.id,
              debateId: id
            });
            startPollingForMessage(id, message.id);
          }
        });
      }
      
      // NEW: Check for CUBE AI response in progress and resume polling
      if (response.cubeChallenge?.isCubeChallenge && response.cubeChallenge.aiResponseInProgress) {
        logger.info('🤖 RESUME CUBE POLLING: Found CUBE AI response in progress on page load, resuming polling', {
          debateId: id
        });
        startPollingForCubeAI(id);
      }
        
        // Update cache using ref to avoid re-renders
        debateCacheRef.current[id] = {
          debate: response,
          timestamp: Date.now()
        };
      
      // Check if we need to update the corresponding list
      if (response.status === 'active') {
        // Replace in active debates list if already there
        setActiveDebates(prev => updateDebateInList(prev, response));
      } else if (response.status === 'completed') {
        // Replace in completed debates list if already there
        setCompletedDebates(prev => updateDebateInList(prev, response));
      }
      
      setLoading(false);
      currentDebateIdRef.current = null;
    } catch (error) {
      logger.error(`DebateContext: Error fetching debate ${id}:`, error);
      setError('Failed to fetch debate');
      setLoading(false);
      currentDebateIdRef.current = null;
      throw error;
      } finally {
        // Clear the active fetch
        delete activeFetchesRef.current[id];
      }
    })();
    
    // Store the promise to prevent duplicate fetches
    activeFetchesRef.current[id] = fetchPromise;
    
    return fetchPromise;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- debateCache would cause infinite loops
  }, []);

  // Add fetchUserDebates function
  const fetchUserDebates = useCallback(async () => {
    if (!user?.id) return;
    
    setIsFetching(prev => ({ ...prev, user: true }));
    setError(null);
    
    try {
      const userDebatesData = await getUserDebates(user.id);
      setUserDebates(userDebatesData);
    } catch (err) {
      logger.error('Error fetching user debates:', err);
      setError('Failed to fetch user debates');
    } finally {
      setIsFetching(prev => ({ ...prev, user: false }));
    }
  }, [user?.id]);

  // Add debounced version of fetchUserDebates
  const debouncedFetchUserDebates = useCallback(() => {
    if (debounceTimeouts.user) {
      clearTimeout(debounceTimeouts.user);
    }
    
    const timeout = setTimeout(() => {
      fetchUserDebates();
    }, 500);
    
    setDebounceTimeouts(prev => ({ ...prev, user: timeout }));
  }, [debounceTimeouts.user, fetchUserDebates]);

  // Update fetchActiveDebates with improved error handling
  const fetchActiveDebates = useCallback(async () => {
    setIsFetching(prev => ({ ...prev, active: true }));
    setError(null);
    
    try {
      const activeDebatesData = await getActiveDebates();
      setActiveDebates(activeDebatesData);
    } catch (err) {
      logger.error('Error fetching active debates:', err);
      // Don't set error state for unauthorized errors when fetching public debates
      if (axios.isAxiosError(err) && err.response?.status !== 401) {
        setError('Failed to fetch active debates');
      } else if (!axios.isAxiosError(err)) {
        setError('Failed to fetch active debates');
      }
    } finally {
      setIsFetching(prev => ({ ...prev, active: false }));
    }
  }, []);

  // Initialize socket connection with improved settings for Vercel
  useEffect(() => {
    // Skip Socket.IO connection if using Pusher
    if (USE_PUSHER) {
      logger.info('DebateContext: Using Pusher instead of Socket.IO - socket connection skipped');
      return;
    }
    
    // Test if WebSockets work on Vercel
    if (!user?.id) {
      logger.info('DebateContext: No user ID available for socket connection');
      return;
    }
    
    try {
      // Get the appropriate socket URL for the current environment
      const socketUrl = getSocketUrl();
      logger.info('DebateContext: ------------------------------------------------------');
      logger.info('DebateContext: ATTEMPTING SOCKET CONNECTION TO:', socketUrl);
      logger.info('DebateContext: WITH PATH: /socket.io/');
      logger.info('DebateContext: ------------------------------------------------------');
    
      // Initialize socket connection with improved settings for Vercel
      const socketConnection = io(socketUrl, {
        transports: ['websocket', 'polling'],  // Try WebSocket first, fallback to polling
        path: '/socket.io/', // Standard Socket.IO path
        query: {
          userId: user.id
        },
        reconnectionAttempts: 10,       // Try to reconnect 10 times
        reconnectionDelay: 1000,        // Start with 1 second delay
        reconnectionDelayMax: 10000,    // Maximum 10 second delay
        timeout: 30000,                 // Increased timeout for Vercel
        // Add critical settings for Vercel/serverless environments
        autoConnect: true,              // Auto connect on creation
        forceNew: true,                 // Force a new connection every time
        extraHeaders: {                 // Add extra headers for identification
          'X-Client-Version': '1.0.0',
          'X-Client-Type': 'web'
        }
      });
      
      // Add explicit logging
      logger.info('DebateContext: Attempting Socket.IO connection with config:', {
        url: socketUrl,
        path: '/socket.io/',
        fullUrl: `${socketUrl}/socket.io/`,
        transports: ['websocket', 'polling']
      });
        
      // Log socket connection events
      socketConnection.on('connect', () => {
        logger.info('DebateContext: Socket connected successfully ✅', socketConnection.id);
        setSocket(socketConnection);
        toast.success('WebSocket connected successfully!', { id: 'socket-connect' });
        
        // Manually set socket.io options for ping interval if needed
        try {
          // @ts-ignore - This is a workaround for Socket.IO private properties
          if (socketConnection.io && socketConnection.io.engine) {
            // @ts-ignore - Access internal properties to modify ping settings
            socketConnection.io.engine.pingTimeout = 30000; // 30 seconds
            // @ts-ignore - Access internal properties to modify ping settings
            socketConnection.io.engine.pingInterval = 25000; // 25 seconds
            logger.info('DebateContext: Socket pingTimeout and pingInterval set manually');
          }
        } catch (err) {
          logger.warn('DebateContext: Could not set socket ping configuration:', err);
        }
      });
      
      socketConnection.on('connect_error', (error) => {
        logger.error('DebateContext: Socket connection error ❌', error);
        
        // Add detailed error information with type-safe property access
        const errorInfo = {
          message: error.message,
          // Safely access properties that might not exist on standard Error type
          description: (error as any).description || 'No description available',
          context: (error as any).context || 'No context available',
          type: (error as any).type || 'Unknown error type',
          socketUrl: socketUrl,
          transport: socketConnection.io.engine?.transport?.name || 'unknown',
          hostname: window.location.hostname,
          protocol: window.location.protocol,
          isHttps: window.location.protocol === 'https:',
          userAgent: navigator.userAgent
        };
        
        // Log diagnostic information about the connection attempt
        logger.error('DebateContext: Connection attempt details:', {
          socketUrl,
          path: '/socket.io/',
          fullUrl: `${socketUrl}/socket.io/`,
          hasUserID: !!user?.id,
          errorInfo
        });
        
        logger.error('DebateContext: Detailed socket error info:', errorInfo);
        
        // More user-friendly error message with details
        toast.error(`WebSocket error: ${error.message} [${errorInfo.transport}]`, { 
          id: 'socket-error',
          duration: 5000 
        });
      });
      
      socketConnection.on('disconnect', (reason) => {
        logger.info('DebateContext: Socket disconnected ⚠️', reason);
        
        // Only show disconnect toast for unexpected disconnects
        if (reason !== 'io client disconnect' && reason !== 'io server disconnect') {
          toast.error('WebSocket disconnected: ' + reason, { id: 'socket-disconnect' });
          
          // Attempt to reconnect automatically for certain disconnect reasons
          if (
            reason === 'transport close' || 
            reason === 'transport error' || 
            reason === 'ping timeout'
          ) {
            logger.info('DebateContext: Attempting to reconnect socket automatically...');
            setTimeout(() => {
              socketConnection.connect();
            }, 1000);
          }
        }
      });
      
      // Add reconnect event handlers
      socketConnection.io.on('reconnect', (attempt) => {
        logger.info(`DebateContext: Socket reconnected after ${attempt} attempts ✅`);
        toast.success('WebSocket reconnected!', { id: 'socket-reconnect' });
        
        // If we're in a debate, rejoin the room
        if (currentDebate?.id) {
          logger.info('DebateContext: Rejoining debate room after reconnection');
          socketConnection.emit('join_debate', currentDebate.id, user.id);
        }
      });
      
      socketConnection.io.on('reconnect_attempt', (attempt) => {
        logger.info(`DebateContext: Socket reconnect attempt ${attempt}`);
      });
      
      socketConnection.io.on('reconnect_error', (error) => {
        logger.error('DebateContext: Socket reconnection error:', error);
      });
      
      socketConnection.io.on('reconnect_failed', () => {
        logger.error('DebateContext: Socket reconnection failed after all attempts');
        toast.error('WebSocket reconnection failed after multiple attempts', { id: 'socket-reconnect-failed' });
      });
      
      // Clean up socket connection on unmount
      return () => {
        logger.info('DebateContext: Closing socket connection');
        socketConnection.disconnect();
        setSocket(null);
      };
    } catch (error) {
      logger.error('DebateContext: Error initializing socket:', error);
      toast.error('Failed to initialize WebSocket');
    }
  }, [user?.id, currentDebate?.id]);

  // Add a new effect to detect debate ID changes and reconnect if needed
  useEffect(() => {
    // Skip if using Pusher
    if (USE_PUSHER) {
      logger.info('DebateContext: Using Pusher instead of Socket.IO - socket reconnection for debate change skipped');
      return;
    }
    
    // If debate ID changed, reconnect the socket
    if (currentDebate?.id && previousDebateIdRef.current !== currentDebate.id && user?.id) {
      logger.info('DebateContext: Debate ID changed, reconnecting socket...');
      
      // Disconnect existing socket if any
      if (socket) {
        logger.info('DebateContext: Closing existing socket connection due to debate change');
        socket.disconnect();
        setSocket(null);
      }
      
      // Short timeout to ensure clean reconnection
      setTimeout(() => {
        try {
          // Get the appropriate socket URL for the current environment
          const socketUrl = getSocketUrl();
          logger.info('DebateContext: Reconnecting to socket server at:', socketUrl);
          
          const socketConnection = io(socketUrl, {
            transports: ['websocket', 'polling'],
            path: '/socket.io/',
            query: {
              userId: user.id,
              debateId: currentDebate.id
            },
            reconnectionAttempts: 10,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 10000,
            timeout: 20000,
            autoConnect: true,
            forceNew: true,
            extraHeaders: {
              'X-Client-Version': '1.0.0',
              'X-Client-Type': 'web'
            }
          });
          
          socketConnection.on('connect', () => {
            logger.info('DebateContext: Socket reconnected for new debate ✅', socketConnection.id);
            setSocket(socketConnection);
            toast.success('WebSocket connected for debate!', { id: 'socket-debate-change' });
            
            // Manually set socket.io options for ping interval
            try {
              // @ts-ignore - This is a workaround for Socket.IO private properties
              if (socketConnection.io && socketConnection.io.engine) {
                // @ts-ignore - Access internal properties to modify ping settings
                socketConnection.io.engine.pingTimeout = 30000; // 30 seconds
                // @ts-ignore - Access internal properties to modify ping settings
                socketConnection.io.engine.pingInterval = 25000; // 25 seconds
              }
            } catch (err) {
              logger.warn('DebateContext: Could not set socket ping configuration:', err);
            }
            
            // Force a fetch of the current debate to ensure we have latest messages
            fetchDebateById(currentDebate.id, true);
          });
          
          socketConnection.on('connect_error', (error) => {
            logger.error('DebateContext: Socket reconnection error on debate change ❌', error);
            toast.error('WebSocket connection failed: ' + error.message, { id: 'socket-debate-change-error' });
          });
        } catch (error) {
          logger.error('DebateContext: Error reconnecting socket for debate change:', error);
        }
      }, 300);
        
      // Update the ref
      previousDebateIdRef.current = currentDebate.id;
    }
  }, [currentDebate?.id, user?.id, socket, fetchDebateById]);

  // Add a new effect to ensure socket reconnection when entering a debate room
  useEffect(() => {
    // Skip if using Pusher
    if (USE_PUSHER) {
      logger.info('DebateContext: Using Pusher instead of Socket.IO - socket reconnection for debate entry skipped');
      return;
    }
    
    // This effect will run whenever the current debate changes
    if (currentDebate?.id && user?.id && !socket) {
      logger.info('DebateContext: Detected debate entry without active socket, reconnecting...');
      
      try {
        // Get the appropriate socket URL for the current environment
        const socketUrl = getSocketUrl();
        logger.info('DebateContext: Reconnecting to socket server at:', socketUrl);
        
        const socketConnection = io(socketUrl, {
          transports: ['websocket', 'polling'],
          path: '/socket.io/',
          query: {
            userId: user.id,
            debateId: currentDebate.id // Include debate ID in connection
          },
          reconnectionAttempts: 10,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 10000,
          timeout: 20000,
          autoConnect: true,
          forceNew: true,
          extraHeaders: {
            'X-Client-Version': '1.0.0',
            'X-Client-Type': 'web'
          }
        });
        
        socketConnection.on('connect', () => {
          logger.info('DebateContext: Socket reconnected for debate room ✅', socketConnection.id);
          setSocket(socketConnection);
          toast.success('WebSocket connected for debate room!', { id: 'socket-debate-connect' });
          
          // Manually set socket.io options for ping interval
          try {
            // @ts-ignore - This is a workaround for Socket.IO private properties
            if (socketConnection.io && socketConnection.io.engine) {
              // @ts-ignore - Access internal properties to modify ping settings
              socketConnection.io.engine.pingTimeout = 30000; // 30 seconds
              // @ts-ignore - Access internal properties to modify ping settings
              socketConnection.io.engine.pingInterval = 25000; // 25 seconds
            }
          } catch (err) {
            logger.warn('DebateContext: Could not set socket ping configuration:', err);
          }
          
          // Force a fetch of the current debate to ensure we have latest messages
          fetchDebateById(currentDebate.id, true);
        });
        
        socketConnection.on('connect_error', (error) => {
          logger.error('DebateContext: Socket reconnection error ❌', error);
          toast.error('WebSocket connection failed: ' + error.message, { id: 'socket-debate-error' });
        });
      } catch (error) {
        logger.error('DebateContext: Error reconnecting socket for debate room:', error);
      }
    }
  }, [currentDebate?.id, user?.id, socket, fetchDebateById]);

  // Polling mechanism for updates - DISABLED FOR EXPERIMENTS
  useEffect(() => {
    logger.info('DebateContext: Polling is disabled for experiments');
    // No polling will happen in this version
    return;
  }, [currentDebate?.id]);

  // Socket.IO event listeners - DISABLED to eliminate dual-channel confusion with Pusher
  // All real-time updates now handled via Pusher only
  useEffect(() => {
    if (!socket) {
      logger.info('DebateContext: No socket available');
      return;
    }
    
    logger.info('DebateContext: Socket.IO listeners DISABLED - using Pusher only for real-time updates');
    
    // DISABLED: All Socket.IO event listeners to prevent dual-channel race conditions
    // Real-time updates are now handled exclusively through Pusher
    
    return () => {
      logger.info('DebateContext: Socket.IO cleanup (no listeners to remove)');
    };
  }, [socket, currentDebate, fetchDebateById]);

  // Update fetchDebates with similar anti-duplication logic
  const fetchDebates = async () => {
    // Skip if already fetching
    if (isFetching.debates) {
      logger.info('DebateContext: Already fetching debates, skipping duplicate request');
      return;
    }
    
    setIsFetching(prev => ({ ...prev, debates: true }));
    setLoading(true);
    setError(null);
    
    try {
      logger.info('DebateContext: Fetching all debates...');
      const data = await getDebates();
      // Filter out any invalid debates before setting state
      const validDebates = data.filter(debate => debate && debate.id);
      setDebates(validDebates);
      logger.info(`DebateContext: Successfully fetched ${validDebates.length} debates`);
    } catch (err) {
      setError('Failed to fetch debates');
      logger.error('Error fetching debates:', err);
    } finally {
      setLoading(false);
      setIsFetching(prev => ({ ...prev, debates: false }));
    }
  };

  // Start a new debate
  const startNewDebate = async (
    topic: string,
    description?: string,
    additionalData?: {
      views?: { prompt: string; counterPrompt: string }[];
      teams?: { name: string; viewpoint: string; isAI: boolean; maxMembers?: number; publicSpots?: number }[];
      parameters?: {
        numberOfTeams: number;
        topic: string;
        timeLimit?: number;
        maxMessages?: number;
      };
      isPublic?: boolean;
      publicJoinable?: boolean;
      invitePasswordEnabled?: boolean;
      invitePassword?: string;
    }
  ): Promise<Debate> => {
    setLoading(true);
    setError(null);
    try {
      const { invitePasswordEnabled, invitePassword, ...rest } = additionalData || {};
      const newDebate = await createDebate({
        topic,
        description,
        ...rest,
        ...(invitePasswordEnabled ? { invitePasswordEnabled, invitePassword } : {})
      });
      setDebates(prevDebates => [newDebate, ...prevDebates]);
      return newDebate;
    } catch (err) {
      setError('Failed to create debate');
      logger.error('Failed to create debate', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Generate debate views
  const generateViews = async (topic: string, description?: string): Promise<DebateView> => {
    setLoading(true);
    setError(null);
    try {
      const views = await generateDebateViews(topic, description);
      return views;
    } catch (err) {
      setError('Failed to generate debate views');
      logger.error('Failed to generate debate views', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Helper function to send a message
  const sendMessage = useCallback(async (
    debateId: string, 
    content: string, 
    teamId: string, 
    isReformulation: boolean = false
  ) => {
    // Get token from localStorage to ensure it's the latest
    const token = localStorage.getItem('token');
    
    logger.info('DebateContext: Sending message', {
        debateId, 
        teamId, 
      isReformulation,
      contentPreview: content.substring(0, 50) + (content.length > 50 ? '...' : ''),
      hasToken: !!token,
      socketAvailable: !!socket?.connected
    });
    
    if (!token) {
      logger.error('DebateContext: No auth token available when sending message');
    }
    
    // Create a temporary ID to track this message while processing
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Add to processing messages state
    setProcessingMessages(prev => ({
      ...prev,
      [tempId]: true
    }));
    
    // Add temporary message to UI to show it's being processed
    setCurrentDebate(prev => {
      if (!prev) return prev;
      
      const tempMessage = {
        id: tempId,
        _id: tempId,
        content,
        teamId,
        senderId: user?.id,
        flags: [],
        createdAt: new Date().toISOString(),
        isReformulation,
        isProcessing: true // Mark as processing
      };
      
      return {
        ...prev,
        messages: [...prev.messages, tempMessage]
      };
    });
    
    // Show processing toast
    const toastId = toast.loading('Analyzing message...');
    
    try {
      // Try to send via socket if available, otherwise fall back to HTTP
      let messageResponse: Message;
      
          // Use HTTP directly if Pusher is enabled (skip Socket.IO attempt)
    if (USE_PUSHER || !socket?.connected) {
      logger.info('DebateContext: Using HTTP API to send message', {
        USE_PUSHER,
        socketConnected: socket?.connected,
        reason: USE_PUSHER ? 'Pusher enabled' : 'Socket not connected'
      });
      messageResponse = await sendViaHttp();
      } else {
        try {
          logger.info('DebateContext: Sending message via socket');
          
          // Create a promise to wait for acknowledgement
          messageResponse = await new Promise((resolve, reject) => {
            // Set a timeout in case the server doesn't respond
            const timeoutId = setTimeout(() => {
              logger.info('DebateContext: Socket timeout, falling back to HTTP');
              // Fall back to HTTP if socket times out
              sendViaHttp().then(resolve).catch(reject);
            }, 45000); // Allow time for AI flag analysis (under 60s platform limit)
            
            // Emit the message via socket with acknowledgement callback
            socket.emit('new_message', {
              debateId,
              content,
              teamId,
              isReformulation
            }, (response: any) => {
              clearTimeout(timeoutId);
              if (response && response.error) {
                logger.error('DebateContext: Socket error response:', response.error);
                // Fall back to HTTP if socket returns an error
                sendViaHttp().then(resolve).catch(reject);
              } else if (response && response.success) {
                logger.info('DebateContext: Message sent successfully via socket');
                resolve(response.message);
              } else {
                logger.info('DebateContext: No valid response from socket, falling back to HTTP');
                sendViaHttp().then(resolve).catch(reject);
              }
            });
          });
        } catch (err) {
          logger.error('DebateContext: Socket message emission failed:', err);
          // Fall back to HTTP API
          messageResponse = await sendViaHttp();
        }
      }
      
      // Remove the temporary message and replace with real message
      setCurrentDebate(prev => {
        if (!prev) return prev;
        
        // Filter out the temporary message
        const filteredMessages = prev.messages.filter(msg => 
          msg.id !== tempId && msg._id !== tempId
        );
        
        // Add the real message (if it's not already included via socket)
        const messageExists = filteredMessages.some(msg => 
          (msg.id === messageResponse.id) || (msg._id === messageResponse.id)
        );
        
        if (!messageExists) {
          // Keep the message in processing state until AI analysis completes
          const messageWithProcessing = {
            ...messageResponse,
            isProcessing: true // Keep processing state
          };
          
          logger.info('DebateContext: Added message with processing state:', {
            messageId: messageWithProcessing.id,
            isProcessing: messageWithProcessing.isProcessing,
            content: messageWithProcessing.content?.substring(0, 30)
          });
          
          return {
            ...prev,
            messages: [...filteredMessages, messageWithProcessing]
          };
        }
        
        return {
          ...prev,
          messages: filteredMessages
        };
      });
      
      // Don't remove from processing messages yet - wait for AI analysis
      // setProcessingMessages(prev => {
      //   const updated = {...prev};
      //   delete updated[tempId];
      //   return updated;
      // });
      
      // Keep message in processing state until AI analysis completes
      // No timeout - let Pusher handle the completion notification
      // This prevents race conditions where users respond before flags are detected
      
      // Show a different toast for processing
      toast.success('Message sent! AI analysis in progress...', { id: toastId });
      
      // Start polling for AI analysis completion
      const messageId = messageResponse.id || messageResponse._id;
      if (messageId) {
        startPollingForMessage(debateId, messageId);
      } else {
        logger.error('No message ID available for polling');
      }
      
      // NEW: For CUBE challenges, also start polling for AI defender response
      if (currentDebate?.cubeChallenge?.isCubeChallenge) {
        // Check if this message is from the challenger (not AI)
        const aiTeam = currentDebate.teams.find(team => team.isAI);
        const isFromChallenger = teamId !== aiTeam?.id;
        
        if (isFromChallenger) {
          logger.info('🤖 CUBE: Challenger sent message, starting AI response polling');
          startPollingForCubeAI(debateId);
        }
      }
      
      return messageResponse;
    } catch (err: any) {
      // Remove from processing messages
      setProcessingMessages(prev => {
        const updated = {...prev};
        delete updated[tempId];
        return updated;
      });
      
      // Remove temporary message on error
          setCurrentDebate(prev => {
            if (!prev) return prev;
            
            return {
              ...prev,
          messages: prev.messages.filter(msg => msg.id !== tempId)
            };
          });
      
      toast.error('Failed to send message', { id: toastId });
      logger.error('DebateContext: Error sending message:', err);
      logger.error('DebateContext: Error details:', err.response?.data);
      throw err;
    }
    
    // Helper function to send via HTTP API
    async function sendViaHttp() {
      try {
        // Ensure headers include the token
        const headers = {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        };
        
        logger.info('DebateContext: Sending message via HTTP API');
        
        const response = await axios.post(
          makeApiPath(`/debates/${debateId}/messages`), 
          { content, teamId, isReformulation },
          { headers }
        );
        
        // Check if the response has message data
        if (response.data) {
          logger.info('DebateContext: Message sent successfully via HTTP, got response:', {
            ...response.data,
            content: response.data.content?.substring(0, 30),
            isProcessing: response.data.isProcessing
          });
        return response.data;
      } else {
        throw new Error('No message data returned from server');
      }
    } catch (err: any) {
        logger.error('DebateContext: Error sending message via HTTP:', err);
      logger.error('DebateContext: Error details:', err.response?.data);
      throw err;
    }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- startPollingForCubeAI/startPollingForMessage stable
  }, [socket, user?.id]);

  // Lift a flag on a message
  const liftFlag = async (debateId: string, messageId: string, flagId: string) => {
    logger.info('DebateContext: Lifting flag:', { debateId, messageId, flagId, socketAvailable: !!socket?.connected });
    
    // Try to lift flag via socket if available
    if (socket?.connected) {
      try {
        logger.info('DebateContext: Attempting to lift flag via socket');
        
        // Emit the lift_flag event via socket
        socket.emit('lift_flag', {
          debateId,
          messageId,
          flagId
        });
        
        logger.info('DebateContext: Lift flag emitted via socket');
        
        // We'll still make the API call as a fallback, but update the UI immediately
        // for better responsiveness
      } catch (err) {
        logger.error('DebateContext: Socket lift flag emission failed:', err);
      }
    }
    
    try {
      // Make API call to lift flag using authenticated api instance
      const response = await api.put(`/debates/${debateId}/messages/${messageId}/flags/${flagId}/lift`);

      logger.info('DebateContext: Flag lifted successfully, server response:', response.data);
      
      // Update the local state immediately instead of waiting for the socket
      setCurrentDebate(prevDebate => {
        if (!prevDebate || prevDebate.id !== debateId) return prevDebate;
        
        // Make a deep copy of the messages
        const updatedMessages = prevDebate.messages.map(message => {
          // Check both id and _id for compatibility
          if (message.id !== messageId && message._id !== messageId) return message;
          
          // Update the flags array for this message
          const updatedFlags = (message.flags || []).map(flag => {
            // Check both id and _id for compatibility
            if ((flag.id && flag.id === flagId) || (flag._id && flag._id === flagId)) {
              return { ...flag, lifted: true };
            }
            return flag;
          });
          
          return { ...message, flags: updatedFlags };
        });
        
        return {
          ...prevDebate,
          messages: updatedMessages,
        };
      });
    } catch (error) {
      logger.error('DebateContext: Error lifting flag:', error);
      throw error;
    }
  };

  // Update fetchCompletedDebates with similar error handling
  const fetchCompletedDebates = useCallback(async () => {
    setIsFetching(prev => ({ ...prev, completed: true }));
    setError(null);
    
    try {
      const completedDebatesData = await getCompletedDebates();
      setCompletedDebates(completedDebatesData);
    } catch (err) {
      logger.error('Error fetching completed debates:', err);
      // Don't set error state for unauthorized errors when fetching public debates
      if (axios.isAxiosError(err) && err.response?.status !== 401) {
        setError('Failed to fetch completed debates');
      } else if (!axios.isAxiosError(err)) {
        setError('Failed to fetch completed debates');
      }
    } finally {
      setIsFetching(prev => ({ ...prev, completed: false }));
    }
  }, []);

  // Add a debounced version of fetchActiveDebates
  const debouncedFetchActiveDebates = async () => {
    // Clear any existing timeout
    if (debounceTimeouts.active) {
      clearTimeout(debounceTimeouts.active);
    }
    
    // Set a new timeout
    const timeout = setTimeout(() => {
      fetchActiveDebates();
      // Clear the timeout reference
      setDebounceTimeouts(prev => ({ ...prev, active: null }));
    }, 500); // 500ms debounce
    
    // Store the timeout reference
    setDebounceTimeouts(prev => ({ ...prev, active: timeout }));
  };

  // Add a debounced version of fetchCompletedDebates
  const debouncedFetchCompletedDebates = async () => {
    // Clear any existing timeout
    if (debounceTimeouts.completed) {
      clearTimeout(debounceTimeouts.completed);
    }
    
    // Set a new timeout
    const timeout = setTimeout(() => {
      fetchCompletedDebates();
      // Clear the timeout reference
      setDebounceTimeouts(prev => ({ ...prev, completed: null }));
    }, 500); // 500ms debounce
    
    // Store the timeout reference
    setDebounceTimeouts(prev => ({ ...prev, completed: timeout }));
  };

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      // Clear any pending timeouts when component unmounts
      if (debounceTimeouts.active) clearTimeout(debounceTimeouts.active);
      if (debounceTimeouts.completed) clearTimeout(debounceTimeouts.completed);
      if (debounceTimeouts.debates) clearTimeout(debounceTimeouts.debates);
      if (debounceTimeouts.user) clearTimeout(debounceTimeouts.user);
    };
  }, [debounceTimeouts]);

  // Add this helper function to properly handle message addition from socket events (reserved for socket path)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const addMessageToDebate = (debate: Debate, message: any): Debate => {
    if (!debate) return debate;
    
    logger.info('DebateContext: Adding message from socket to debate', {
      debateId: debate.id,
      messageTeamId: message.teamId,
      messageId: message.id || message._id || (message as any).messageId
    });
    
    // Normalize the message ID
    const messageId = message.id || message._id || (message as any).messageId;
    
    // Make sure we have a valid message ID
    if (!messageId) {
      logger.warn('DebateContext: Received message without ID, skipping addition');
      return debate;
    }
    
    // Avoid duplicate messages by checking if this message ID already exists
    const messageExists = debate.messages && debate.messages.some(m => 
      (m.id === messageId) || (m._id === messageId) || ((m as any).messageId === messageId)
    );
    
    if (messageExists) {
      logger.info('DebateContext: Message already exists in debate, skipping addition');
      return debate;
    }
    
    // Normalize the message format
    const normalizedMessage = {
      ...message,
      id: messageId, // Ensure we have an id field
    };
    
    // Create a completely new message array to ensure React detects the change
    const updatedMessages = [...(debate.messages || []), normalizedMessage];
    
    logger.info(`DebateContext: Added message to debate. New message count: ${updatedMessages.length}`);
    
    // Create a new debate object to ensure React detects the change
    return {
      ...debate,
      messages: updatedMessages
    };
  };

  // Helper function to handle AI analysis completion
  const handleAIAnalysisComplete = useCallback((messageId: string, hasFlags: boolean, senderId?: string) => {
    logger.info('DebateContext: AI analysis complete, removing processing state:', {
      messageId,
      hasFlags,
      senderId,
      currentUserId: user?.id
    });
    
    // Remove processing state from the message
    setCurrentDebate(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        messages: prev.messages.map(msg => 
          (msg.id === messageId || msg._id === messageId) 
            ? { ...msg, isProcessing: false }
            : msg
        )
      };
    });
    
    // Show a toast only for the sender; receivers already see visual flags
    const isOwnMessage = !!(senderId && user?.id && String(senderId) === String(user.id));
    if (isOwnMessage) {
      // Deduplicate by id; sender gets only one toast
      const ownFlagId = `own-flag-${messageId}`;
      const ownDoneId = `own-done-${messageId}`;
      if (hasFlags) toast.error('Your message contains logical fallacies!', { id: ownFlagId });
      else toast.success('Message analysis complete!', { id: ownDoneId });
    }
    // Don't show any toast for other users' messages without flags
  }, [user?.id]);

  // Polling function for AI analysis completion
  const pollForAnalysisComplete = useCallback(async (debateId: string, messageId: string, totalElapsed = 0) => {
    try {
      const response = await fetch(`/api/debates/${debateId}/messages/${messageId}/analysis-status`);
      if (!response.ok) {
        logger.error('Failed to check analysis status:', response.status);
        return;
      }
      
      const data = await response.json();
      
      if (data.aiAnalysisComplete) {
        logger.info('AI analysis complete via polling:', data);
        handleAIAnalysisComplete(messageId, data.hasFlags);
        
        // Refresh debate data to get updated flags
        if (fetchDebateById) {
          fetchDebateById(debateId, true);
        }
        return; // Stop polling
      }
      
      // Continue polling with exponential backoff
      let nextDelay;
      if (totalElapsed < 8000) {
        // First 8 seconds: check every 2 seconds
        nextDelay = 2000;
      } else {
        // After 8 seconds: check every 8 seconds
        nextDelay = 8000;
      }
      
      // Stop polling after reasonable time (40 seconds)
      if (totalElapsed < 40000) {
        setTimeout(() => {
          pollForAnalysisComplete(debateId, messageId, totalElapsed + nextDelay);
        }, nextDelay);
              } else {
          logger.warn('Polling timeout - stopping after 40 seconds - assuming analysis failed, marking as complete');
          
          // Fallback: assume analysis failed and mark message as complete without flags
          handleAIAnalysisComplete(messageId, false);
          
          // Show timeout message to user
          toast('AI analysis timeout (40s) - message sent without analysis', { icon: '⚠️' });
          
          // Refresh debate data
          if (fetchDebateById) {
            fetchDebateById(debateId, true);
          }
        }
      
    } catch (error) {
      logger.error('Error polling for analysis completion:', error);
      
      // On error, continue polling but with longer delay
      // Don't give up immediately on network errors
      if (totalElapsed < 40000) {
        const errorDelay = Math.min(totalElapsed + 15000, 30000); // 15s delay on error, max 30s
        setTimeout(() => {
          pollForAnalysisComplete(debateId, messageId, totalElapsed + errorDelay);
        }, errorDelay);
      } else {
        // After 40 seconds of errors, give up
        logger.error('Polling failed after 40 seconds - marking as complete without analysis');
        handleAIAnalysisComplete(messageId, false);
        toast('Network error during analysis (40s timeout) - message sent without analysis', { icon: '❌' });
      }
    }
  }, [handleAIAnalysisComplete, fetchDebateById]);

  // Start polling when a message is sent
  // NEW: Polling function for CUBE AI response
  const startPollingForCubeAI = useCallback((debateId: string) => {
    logger.info('🤖 CUBE: Starting polling for AI response', { debateId });
    
    let attempts = 0;
    const maxAttempts = 112; // 15 minutes with 8-second intervals (15*60/8 = 112.5)
    
    const pollCubeStatus = async () => {
      attempts++;
      logger.info('🤖 CUBE: Polling attempt', { attempt: attempts, maxAttempts });
      
      try {
        const response = await fetch(`/api/debates/${debateId}/cube-ai-status`);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const data = await response.json();
        logger.info('🤖 CUBE: Poll response', data);
        
        // If AI response is no longer in progress, refresh the debate
        if (!data.aiResponseInProgress) {
          logger.info('🤖 CUBE: AI response completed, refreshing debate');
          await fetchDebateById(debateId, true);
          toast.success('AI defender has responded!', { icon: '🤖' });
          return; // Stop polling
        }
        
        // Continue polling if still in progress and under limit
        if (attempts < maxAttempts) {
          setTimeout(pollCubeStatus, 8000); // 8 seconds
        } else {
          logger.warn('🤖 CUBE: Polling timeout reached');
          toast('AI response is taking longer than expected. Try refreshing the page.', { 
            icon: '⏱️',
            duration: 8000 
          });
        }
        
      } catch (error) {
        logger.error('🤖 CUBE: Polling error', error);
        
        // Retry on error (up to limit)
        if (attempts < maxAttempts) {
          setTimeout(pollCubeStatus, 8000);
        } else {
          logger.error('🤖 CUBE: Max polling attempts reached');
          toast.error('Unable to check AI response status. Try refreshing the page.');
        }
      }
    };
    
    // Start polling after a short delay
    setTimeout(pollCubeStatus, 8000); // First poll after 8 seconds
  }, [fetchDebateById]);

  const startPollingForMessage = useCallback((debateId: string, messageId: string) => {
    logger.info('Starting polling for message analysis:', { debateId, messageId });
    // Start polling after 2 seconds
    setTimeout(() => {
      pollForAnalysisComplete(debateId, messageId, 2000);
    }, 2000);
  }, [pollForAnalysisComplete]);

  // NEW: Fallback polling function for receivers (after 15s timeout)
  const startFallbackPolling = useCallback((debateId: string, messageId: string) => {
    logger.info('Starting fallback polling for message analysis:', { debateId, messageId });
    
    // Start fallback polling with 15s already elapsed
    pollForAnalysisComplete(debateId, messageId, 15000);
  }, [pollForAnalysisComplete]);

  // Expose the function in context
  const contextValue: DebateContextType = {
    debates,
    activeDebates,
    completedDebates,
    userDebates,
    currentDebate,
    loading,
    error,
    socket,
    fetchDebates,
    fetchActiveDebates,
    fetchCompletedDebates,
    fetchUserDebates,
    debouncedFetchActiveDebates,
    debouncedFetchCompletedDebates,
    debouncedFetchUserDebates,
    fetchDebateById,
    startNewDebate,
    generateViews,
    sendMessage,
    liftFlag,
    currentTeamWithFlag,
    setCurrentTeamWithFlag,
    processingMessages,
    handleAIAnalysisComplete,
    startPollingForMessage,
    startFallbackPolling, // NEW: Add fallback polling function
    startPollingForCubeAI, // NEW: Add CUBE polling function
  };

  return (
    <DebateContext.Provider value={contextValue}>
      {children}
    </DebateContext.Provider>
  );
};

export default DebateContext; 